"use client";

import { StatusCodes } from "http-status-codes";
import Cookies from "js-cookie";
import moment from "moment";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import React, {
  Dispatch,
  FunctionComponent,
  SetStateAction,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";

import { AppState } from "@/store/store";
import Preloader from "@/app/components/preloader";
import AutoOrderModals from "@/app/ec/product/[basePath]/auto-order/autoOrderModals";
import DeliveryToAndLocation from "@/app/ec/product/[basePath]/deliveryToAndLocation";
import PriceBreakSummary from "@/app/ec/product/[basePath]/priceBreakSummary";
import ProductVariantPicker from "@/app/ec/product/[basePath]/product-variants/productVariantPicker";
import QuestionsAndAnswersSection from "@/app/ec/product/[basePath]/questionsAndAnswers";
import VendorOption from "@/app/ec/product/[basePath]/vendor-options/vendorOption";
import VendorOptions from "@/app/ec/product/[basePath]/vendor-options/vendorOptions";
import VendorSelector from "@/app/ec/product/[basePath]/vendor-options/vendorSelector";
import VendorTiles from "@/app/ec/product/[basePath]/vendor-options/vendorTiles";
import CallToAction, { AddToAutoOrderProps, getAutoOrderTypeByFrequenceId } from "@/app/ec/product/callToAction";
import { ProductDisplayFormat } from "@/app/ec/product/productDisplayFormat";
import { isManufacturerSponsor } from "@/utils/isManufacturerSponsor";

import PinnedAddToCart from "@/app/ec/product/[basePath]/add-to-cart/pinnedAddToCart";
import AutoOrderMessage from "@/app/ec/product/[basePath]/autoOrderMessage";
import getVendorOptions from "@/app/ec/product/[basePath]/vendor-options/getVendorOptions";
import useAnalyticsManager from "@/app/hooks/useAnalyticsManager";
import useMediaQuery, { MD_DOWN_MEDIA_QUERY } from "@/app/hooks/useMediaQuery";
import { useSubscribeToProductMutation } from "@/store/orderApi";
import { classifySubscribeError } from "@/app/account/orders/auto-order/_lib/handleSubscribeResponse";
import { useGetShoppingCartQuery, useModifyShoppingCartMutation } from "@/store/shoppingCartApi";
import { useGetUserQuery } from "@/store/userApi";
import { sendAbTastyEvent } from "@/utils/analytics/abTasty";
import { IncomingAnalyticsProduct } from "@/utils/analyticsEventSender";

import AddToShoppingList from "@/app/ec/product/[basePath]/addToShoppingList";
import BoughtThisMonth from "@/app/ec/product/[basePath]/boughtThisMonth";
import checkAddToCart, { AddToCartStatus } from "@/app/ec/product/[basePath]/checkAddToCart";
import SellThisNow from "@/app/ec/product/[basePath]/sell-this-now";
import calculateRetailPrice from "@/app/ec/product/[basePath]/vendor-options/calculateRetailPrice";
import type { SellProductEligibility } from "@/fetch/getSellProductEligibility";
import { showListToast } from "@/store/addToShoppingListSlice";
import { useAddToShoppingListMutation } from "@/store/shoppingListApi";
import { getCityAndZipcodeCookies } from "@/utils/locationDetails";
import pdpStyles from "../productDetailPage.module.scss";
import styles from "./productDetailClientPage.module.scss";

const ProductDetailClientPage: FunctionComponent<VendorPickerProps> = ({
  availabilityState,
  vendorOptionsFromServer,
  initialChosenVendorKey,
  hasVidFromServer = false,
  productDetails,
  qty,
  categoriesTree,
  mpId,
  retailPrice,
  variantGroupDetails,
  productSubscriptionFutureDates,
  deliveryToUser,
  maxInventoryAvailable,
  manufacturer,
  productBuyBoxPreference = [],
  sellProductEligibility = null,
  format = "pdp",
}) => {
  const isReviewsFormat = format === "reviewsPage";
  const router = useRouter();
  const dispatch = useDispatch();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [isVariantNavigationPending, setIsVariantNavigationPending] = useState(false);
  const { data: user } = useGetUserQuery();
  const callToActionRef = useRef<HTMLDivElement>(null);
  const { dispatchAddToAutoOrderEvent } = useAnalyticsManager();
  const [modifyShoppingCart, modifyShoppingCartMutationState] = useModifyShoppingCartMutation();
  const [quantity, setQuantity] = useState<number>(qty);
  const [vendorProductId, setVendorProductId] = useState(0);
  const [deliveryData, setDeliveryData] = useState(deliveryToUser);
  const [chosenVendorKey, setChosenVendorKey] = useState<number>(initialChosenVendorKey ? initialChosenVendorKey : 0);
  const [isAddingToCart, setIsAddingToCart] = useState<boolean>(false);
  const [showPinnedAddToCart, setShowPinnedAddToCart] = useState(false);
  const isMobileViewport = useMediaQuery(MD_DOWN_MEDIA_QUERY);
  const isNavVisible = useSelector((state: AppState) => state.navVisibilitySlice.isNavVisible);
  const [chosenAutoOrderLevel, setChosenAutoOrderLevel] = useState<number>(0);
  const [vendorOptions, setVendorOptions] = useState(vendorOptionsFromServer);
  const latestVendorOptionsRequestRef = useRef(0);
  const [vendorOffersExpanded, setVendorOffersExpanded] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [showProductAddedToAOModal, setShowProductAddedToAOModal] = useState(false);
  const [showAOMissingBillingError, setShowAOMissingBillingError] = useState(false);
  const [showAOMissingLicenseError, setShowAOMissingLicenseError] = useState(false);
  const [showAOMissingShippingError, setShowAOMissingShippingError] = useState(false);
  const [showAutoOrderCompleteSetupModal, setShowAutoOrderCompleteSetupModal] = useState(false);
  const [vendorProductBeingAddedToCart, setVendorProductBeingAddedToCart] = useState<number | null>(null);
  const [showAddToCartErrorOnCalltoAction, setShowAddToCartErrorOnCallToAction] = useState<boolean>(false);
  const [sponsoredVendorId, setSponsoredVendorId] = useState<number>(-1);
  const [justAddedToAutoOrder, setJustAddedToAutoOrder] = useState<boolean>(false);

  const [subscribeToProduct] = useSubscribeToProductMutation();
  const [AOfrequency, setAOFrequency] = useState("");
  const [nextOrderDate, setNextOrderDate] = useState("");
  const [showAutoOrderMessage, setShowAutoOrderMessage] = useState(false);

  const { data: shoppingCart } = useGetShoppingCartQuery();
  const [addToShoppingList] = useAddToShoppingListMutation();

  // Reset justAddedToAutoOrder flag when subscription data is updated
  useEffect(() => {
    if (
      justAddedToAutoOrder &&
      Array.isArray(productSubscriptionFutureDates) &&
      productSubscriptionFutureDates.length > 0
    ) {
      const subscriptionId = productSubscriptionFutureDates[0]?.SubscriptionId;
      const hasValidSubscriptionId =
        typeof subscriptionId === "string" ? subscriptionId.trim() !== "" : Boolean(subscriptionId);

      if (hasValidSubscriptionId) {
        setJustAddedToAutoOrder(false);
      }
    }
  }, [productSubscriptionFutureDates, justAddedToAutoOrder]);

  // Reset justAddedToAutoOrder flag when product (mpId) changes
  useEffect(() => {
    setJustAddedToAutoOrder(false);
  }, [mpId]);

  useEffect(() => {
    setIsVariantNavigationPending(false);
  }, [mpId]);

  const onVariantNavigationStart = useCallback(() => {
    setIsVariantNavigationPending(true);
  }, []);

  const [inShoppingList, setInShoppingList] = useState(false);

  const productInCart = useMemo(() => {
    const products = !shoppingCart
      ? []
      : shoppingCart.vendorOrders
          .flatMap((vo) => {
            return vo.products;
          })
          .filter((p) => !p.noLongerForSale);
    return products.find((p) => p.mpId === mpId);
  }, [shoppingCart, mpId]);

  const resolvedSponsoredVendorId = useMemo(() => {
    if (sponsoredVendorId !== -1) return sponsoredVendorId;

    const tsid = searchParams?.get("tsid");
    if (!tsid) return undefined;

    // Manufacturer sponsor
    if (isManufacturerSponsor(tsid)) return tsid;

    // Vendor offering
    const vendorId = Number(tsid);
    return isNaN(vendorId) ? undefined : vendorId;
  }, [sponsoredVendorId, searchParams]);

  const isManufacturerSponsoredPdp =
    typeof resolvedSponsoredVendorId === "string" && isManufacturerSponsor(resolvedSponsoredVendorId);

  /** Positive numeric sponsor id from URL or state; excludes manufacturer `tsid` strings. */
  const numericSponsoredVendorId =
    typeof resolvedSponsoredVendorId === "number" && resolvedSponsoredVendorId > 0
      ? resolvedSponsoredVendorId
      : undefined;

  const { dispatchModifyCartEvent } = useAnalyticsManager();

  useEffect(() => {
    if (modifyShoppingCartMutationState.isSuccess) {
      setIsAddingToCart(false);
      setVendorProductBeingAddedToCart(null);
      router.push(`/shopping-cart-acknowledgement/${mpId}/${vendorProductId}`);
    }
  }, [modifyShoppingCartMutationState, mpId, router, vendorProductId]);

  useEffect(() => {
    const evaluatePinnedBar = () => {
      const cta = document.getElementById("add-to-cart-cta");
      if (!cta) return;
      const ctaScrolledOff = cta.getBoundingClientRect().bottom < 0;
      // mobile only: hide the pinned bar while the bottom nav shares the bottom edge (no nav at md+).
      const hiddenForBottomNav = isMobileViewport && isNavVisible;
      setShowPinnedAddToCart(ctaScrolledOff && !hiddenForBottomNav);
    };
    evaluatePinnedBar();
    window.addEventListener("scroll", evaluatePinnedBar, { passive: true });
    return () => window.removeEventListener("scroll", evaluatePinnedBar);
  }, [isMobileViewport, isNavVisible]);

  useEffect(() => {
    // tsid ("TopSortId") - we wanted the param holding the id of the sponsor to not be obvious
    // set the sponsored vendorId if present in the url
    const isSponsored = searchParams?.has("tsid");
    if (isSponsored && searchParams) {
      const sponsoredVid = searchParams.get("tsid");
      if (sponsoredVid) {
        const numberId = Number(sponsoredVid);
        if (numberId && !isNaN(numberId)) {
          setSponsoredVendorId(numberId);
        }
      }
    }
  }, [searchParams, vendorOptions]);

  const removeURLParamsOnAction = () => {
    const paramsToRemoveOnAction: string[] = ["vid"];

    const url = new URL(window.location.href);
    const hadVid = url.searchParams.has("vid");

    paramsToRemoveOnAction.forEach((p) => {
      url.searchParams.delete(p);
    });

    if (hadVid) {
      window.history.replaceState({}, "", url.toString());
    }
  };

  const getVendorOptionsFromBackend = useCallback(
    async (mpId: number, quantity: number, shouldRemoveVid: boolean = false): Promise<VendorOptionState[] | null> => {
      const requestId = ++latestVendorOptionsRequestRef.current;
      const zipCode = Cookies.get("n32UserPrimaryZip") || "27513";
      setIsCalculating(true);
      const vendorOptionsFromBackend = await getVendorOptions(
        mpId,
        quantity,
        zipCode,
        manufacturer,
        isManufacturerSponsoredPdp,
      );
      if (requestId !== latestVendorOptionsRequestRef.current) {
        return null;
      }
      setIsCalculating(false);
      if (vendorOptionsFromBackend.length === 0) {
        const url = new URL(window.location.href);
        const newUrl = `${url.pathname}?quantity=${quantity}`;
        // Use location.replace to trigger a reload with the updated URL
        window.location.replace(newUrl);

        return null;
      } else {
        setVendorOptions(vendorOptionsFromBackend);
        if (shouldRemoveVid) {
          removeURLParamsOnAction();
        }

        return vendorOptionsFromBackend;
      }
    },
    [manufacturer, isManufacturerSponsoredPdp],
  );

  const setVendorKeyFromId = (
    vendorId: number,
    actualQuantity: number,
    vendorOptionsFromBackend: VendorOptionState[],
  ) => {
    const sponsoredVendorOption = vendorOptionsFromBackend.find((option) => {
      return option.vendorId === vendorId;
    });
    if (sponsoredVendorOption) {
      const hasSponsoredQuantity = checkAddToCart(
        actualQuantity,
        sponsoredVendorOption.minimumQuantityPurchasable,
        sponsoredVendorOption.inventory,
      );
      // force the sponsored vendor option as the chose vendor if it supports the quantity
      if (hasSponsoredQuantity === AddToCartStatus.OK) {
        const matchedVendorKey: number = vendorOptionsFromBackend.findIndex((vendor) => {
          return vendor.vendorId === sponsoredVendorOption.vendorId;
        });
        if (matchedVendorKey !== -1) {
          setChosenVendorKey(matchedVendorKey);
        }
      } else {
        setChosenVendorKey(0);
      }
    } else {
      setChosenVendorKey(0);
    }
  };

  const updateQuantity: Dispatch<SetStateAction<number>> = (newQuantity) => {
    const actualQuantity = typeof newQuantity === "function" ? null : newQuantity;
    if (actualQuantity != null) {
      setQuantity(actualQuantity);
      getVendorOptionsFromBackend(mpId, actualQuantity, true).then((vendorOptionsFromBackend) => {
        if (!vendorOptionsFromBackend) {
          return;
        }
        if (numericSponsoredVendorId !== undefined) {
          setVendorKeyFromId(numericSponsoredVendorId, actualQuantity, vendorOptionsFromBackend);
        } else {
          setChosenVendorKey(0);
        }
      });
    }
  };

  const addToCart = useCallback(
    async (vendorProductToAdd: VendorOptionState) => {
      setIsAddingToCart(true);
      setVendorProductId(vendorProductToAdd.vendorProductId);
      setVendorProductBeingAddedToCart(vendorProductToAdd.vendorProductId);
      const productToAdd: ModifyCartProduct = {
        mpId: mpId as number,
        vendorProductId: vendorProductToAdd.vendorProductId,
        quantity: quantity,
        minimumQuantity: vendorProductToAdd.minimumQuantityPurchasable,
        categoryName: categoriesTree[categoriesTree.length - 1].name,
      };
      const result = await modifyShoppingCart({
        productsToModify: [productToAdd],
        incrementQuantity: false,
      }).unwrap();
      if (result.status === StatusCodes.OK) {
        const productAddedToCart: IncomingAnalyticsProduct = {
          mpId: mpId,
          productTitle: productDetails.title,
          unitPrice: vendorProductToAdd.chosenPriceBreakUnitPrice,
          quantity: quantity,
          originalQuantity: productInCart ? productInCart.quantity : 0,
          brandName: productDetails.brandName,
          categoryList: categoriesTree.map((c) => c.name),
        };
        dispatchModifyCartEvent([productAddedToCart], "Product Detail Page");
        void sendAbTastyEvent(AB_TASTY_ADD_TO_CART_TRACKER);
        const url = new URL(window.location.href);
        const queryId = url.searchParams.get("queryID");
        router.push(
          `/shopping-cart-acknowledgement/${mpId}/${vendorProductToAdd.vendorProductId}${
            queryId ? `?queryID=${queryId}` : ""
          }`,
        );
      } else {
        setIsAddingToCart(false);
        setVendorProductBeingAddedToCart(null);
        alert("An unknown error occurred.  Please try again.");
      }
    },
    [
      categoriesTree,
      dispatchModifyCartEvent,
      modifyShoppingCart,
      mpId,
      productDetails.brandName,
      productDetails.title,
      productInCart,
      quantity,
      router,
    ],
  );

  const changeVendorHandler = (index: number) => {
    setChosenVendorKey(index);
  };

  const getDeliveryLocationAndThenVendorOptions = useCallback(
    async (mpId: number) => {
      const { city, zipCode } = getCityAndZipcodeCookies();
      setDeliveryData({ zipCode, city });
      if (!zipCode) {
        getVendorOptions(mpId, quantity, "27513", manufacturer, isManufacturerSponsoredPdp);
      } else {
        getVendorOptions(mpId, quantity, zipCode, manufacturer, isManufacturerSponsoredPdp);
      }
    },
    [quantity, manufacturer, isManufacturerSponsoredPdp],
  );

  const addToAutoOrder = useCallback(
    async (productToSubscribe: Pick<AddToAutoOrderProps, "frequencyId" | "quantity">, source: string) => {
      if (user?.IsGuestUser || (user?.IsGuestUser === false && user?.isAuthorized === false)) {
        router.push("/login?origin=" + pathname);
        return;
      }
      // Subscribe is recurring-only; one-time is add-to-cart and never reaches here.
      if (productToSubscribe.frequencyId === "one-time") return;

      const subscriptionRequest = {
        subscriberId: user?.UserId,
        mpId: mpId,
        licenseRequired: productDetails.mpLicenseExist || productDetails.catLicenseExist,
        ...productToSubscribe,
        frequencyId: productToSubscribe.frequencyId,
      };
      try {
        // Modern users-service subscribe (POST /users/subscriptions): only HTTP
        // 201 resolves here; 409 / 422 / 401 / 500 reject into the catch below.
        const response = await subscribeToProduct(subscriptionRequest).unwrap();

        const fullSource = "Product Detail Page - " + source;
        dispatchAddToAutoOrderEvent(
          subscriptionRequest.mpId,
          subscriptionRequest.frequencyId,
          subscriptionRequest.quantity,
          fullSource,
        );

        const frequencyById = getAutoOrderTypeByFrequenceId(subscriptionRequest?.frequencyId);
        setAOFrequency(frequencyById?.description);
        // nextOrderDate comes from the 201 response body (ISO yyyy-MM-dd).
        if (response?.nextOrderDate) {
          setNextOrderDate(moment(response.nextOrderDate).format("MM/DD/YYYY"));
        }

        setShowProductAddedToAOModal(true);
        setShowAutoOrderMessage(true);
        getDeliveryLocationAndThenVendorOptions(mpId);

        // Reset auto order level to one-time purchase + hide the options box
        setChosenAutoOrderLevel(0);
        setQuantity(1);
        setJustAddedToAutoOrder(true);

        // Refresh the page to update subscription future dates
        router.refresh();

        // Fallback: reset flag after a timeout if useEffect doesn't trigger
        setTimeout(() => {
          setJustAddedToAutoOrder(false);
        }, 5000);
      } catch (error) {
        const outcome = classifySubscribeError(error);
        switch (outcome.kind) {
          case "INVALID_USER":
            // 401 -> the global baseApi 401 interceptor + SessionExpiryHandler
            // own the login redirect for users/subscriptions traffic (see
            // baseApi.ts AUTO_ORDER_AUTH_PATH_PREFIXES). Don't double-navigate.
            break;
          case "ALREADY_SUBSCRIBED":
            alert("Product already exists in Auto Order, so cannot be added.");
            break;
          case "NEEDS_INFO":
            setShowAOMissingShippingError(outcome.missingShipping);
            setShowAOMissingBillingError(outcome.missingPayment);
            setShowAOMissingLicenseError(outcome.missingLicense);
            setShowAutoOrderCompleteSetupModal(true);
            break;
          default:
            alert("An unknown error has occurred.  Please try again in a few moments.");
            break;
        }
      }
    },
    [
      user?.IsGuestUser,
      user?.isAuthorized,
      user?.UserId,
      mpId,
      productDetails.mpLicenseExist,
      productDetails.catLicenseExist,
      router,
      pathname,
      subscribeToProduct,
      dispatchAddToAutoOrderEvent,
      getDeliveryLocationAndThenVendorOptions,
    ],
  );

  const refreshDeliveryToUser = () => {
    const { city, zipCode } = getCityAndZipcodeCookies();
    setDeliveryData({ zipCode, city });
    if (zipCode?.slice(0, 5) !== deliveryData?.zipCode?.slice(0, 5)) {
      updateQuantity(quantity);
    }
  };

  const setCookieAndFetchOptions = (elementId: string, cookieName: string, mpId: number, quantity: number) => {
    const element = document.getElementById(elementId);
    if (element) {
      const value = element.innerHTML;
      const existingCookie = Cookies.get(cookieName);

      if (!existingCookie || existingCookie !== value) {
        Cookies.set(cookieName, value, { expires: 1 / 24 });
        getVendorOptionsFromBackend(mpId, quantity);
      }

      return setInterval(() => {
        const validCookie = Cookies.get(cookieName);
        if (!validCookie) {
          Cookies.set(cookieName, value, { expires: 1 / 24 });
          getVendorOptionsFromBackend(mpId, quantity);
        }
      }, 1000 * 60);
    }
  };

  // Original event listeners for factorIDChange and applyAuthorizedBoostedChange
  useEffect(() => {
    let factorIdInterval: ReturnType<typeof setInterval> | undefined;
    let authorizedBoostedInterval: ReturnType<typeof setInterval> | undefined;

    const handleFactorIDChange = () => {
      if (factorIdInterval) clearInterval(factorIdInterval);
      factorIdInterval = setCookieAndFetchOptions("factorIdDiv", "buyBoxFactorIdExperiment", mpId, quantity);
    };
    const handleAuthorizedBoostedChange = () => {
      if (authorizedBoostedInterval) clearInterval(authorizedBoostedInterval);
      authorizedBoostedInterval = setCookieAndFetchOptions(
        "applyAuthorizedBoostedDiv",
        "applyAuthorizedBoostedExperiment",
        mpId,
        quantity,
      );
    };

    window.addEventListener("factorIDChange", handleFactorIDChange);
    window.addEventListener("applyAuthorizedBoostedChange", handleAuthorizedBoostedChange);

    return () => {
      if (factorIdInterval) clearInterval(factorIdInterval);
      if (authorizedBoostedInterval) clearInterval(authorizedBoostedInterval);
      window.removeEventListener("factorIDChange", handleFactorIDChange);
      window.removeEventListener("applyAuthorizedBoostedChange", handleAuthorizedBoostedChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mpId, quantity]);

  useEffect(() => {
    setInShoppingList(productDetails.isInShoppingList ?? false);
  }, [productDetails]);

  const onSubmitToShoppingListBackEnd = useCallback(
    async (mpId: number) => {
      try {
        await addToShoppingList([mpId]).unwrap();
        dispatch(showListToast(`"${productDetails.title}" has been added to your shopping list.`));
        setInShoppingList(true);
      } catch {
        dispatch(showListToast({ message: "Failed to add item to shopping list. Please try again.", type: "error" }));
      }
    },
    [addToShoppingList, dispatch, productDetails.title, setInShoppingList, showListToast],
  );

  useEffect(() => {
    const shouldAddToShoppingList = searchParams?.has("addItemToShoppingList");

    if (shouldAddToShoppingList && user?.IsGuestUser === false) {
      onSubmitToShoppingListBackEnd(mpId);
      removeShoppingListURL();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.IsGuestUser]);

  const removeShoppingListURL = async () => {
    const paramsToRemoveOnAction: string[] = ["addItemToShoppingList"];
    const url = new URL(window.location.href);

    paramsToRemoveOnAction.forEach((p) => {
      url.searchParams.delete(p);
    });

    router.replace(url.toString());
  };

  const moveSponsorVendorOptions = () => {
    if (numericSponsoredVendorId === undefined) {
      return vendorOptions;
    }
    const sponsoredVendorOption = vendorOptions.find((vo) => vo.vendorId === numericSponsoredVendorId);
    if (sponsoredVendorOption) {
      const filteredVendorOptions = vendorOptions.filter((o) => o.vendorId !== numericSponsoredVendorId);
      // filter out the sponsor and insert in beginning, this way we don't mutate the original vendorOptions
      return [{ ...sponsoredVendorOption }, ...filteredVendorOptions];
    }

    return vendorOptions;
  };

  return (
    <>
      <div id="factorIdDiv" style={{ display: "none" }}></div> {/*This is for the multi-factor buy box id experiment*/}
      <div id="multiVendorBuyBoxEnabledDiv" style={{ display: "none" }}></div> {/* Tag manager hook */}
      <div id="applyAuthorizedBoostedDiv" style={{ display: "none" }}></div>{" "}
      {/*This is for the multi-factor buy box id experiment*/}
      <SellThisNow
        mpId={mpId}
        vendorOptions={vendorOptions}
        retailPrice={calculateRetailPrice(vendorOptions, productDetails.retailPrice)}
        eligibility={sellProductEligibility}
        onSubmitSuccess={() => getVendorOptionsFromBackend(mpId, 1)}
      />
      {format !== "reviewsPage" && (
        <PinnedAddToCart
          quantity={quantity}
          setQuantity={updateQuantity}
          maxInventoryAvailable={maxInventoryAvailable}
          vendorOptions={vendorOptions}
          mediaPath={productDetails.mediaPath ? productDetails.mediaPath : ""}
          title={productDetails.title}
          bestPrice={retailPrice}
          addToCartCallback={addToCart}
          addToAutoOrderCallback={addToAutoOrder}
          productBeingAddedToCart={isAddingToCart}
          vendorProductBeingAddedToCart={vendorProductBeingAddedToCart}
          vendorOption={vendorOptions[chosenVendorKey] ? vendorOptions[chosenVendorKey] : undefined}
          chosenAutoOrderLevel={chosenAutoOrderLevel}
          setChosenAutoOrderLevel={setChosenAutoOrderLevel}
          showPinnedAddToCart={showPinnedAddToCart}
          chosenVendorKey={chosenVendorKey}
          url={productDetails.url}
          enableErrorAddToCartCallback={() => setShowAddToCartErrorOnCallToAction(true)}
        />
      )}
      <AutoOrderModals
        showCompleteSetupModal={showAutoOrderCompleteSetupModal}
        showAddedToAutoOrderModal={showProductAddedToAOModal}
        showCompleteSetupModalCb={setShowAutoOrderCompleteSetupModal}
        showAddedToAutoOrderModalCb={setShowProductAddedToAOModal}
        missingShippingInfo={showAOMissingShippingError}
        missingBillingInfo={showAOMissingBillingError}
        missingLicenses={showAOMissingLicenseError}
        productTitle={productDetails?.title}
      />
      {(productSubscriptionFutureDates[0]?.SubscriptionId || showAutoOrderMessage) && (
        <Suspense fallback={<></>}>
          <div className={`mb-2 d-flex align-items-center ${pdpStyles["auto-order-status"]}`}>
            <AutoOrderMessage
              data={productSubscriptionFutureDates}
              frequency={AOfrequency}
              nextOrderDate={nextOrderDate}
            />
          </div>
        </Suspense>
      )}
      {availabilityState === "for-sale" && vendorOptions != undefined && vendorOptions && (
        <PriceBreakSummary
          id="pricebreak-summary"
          retailPrice={retailPrice}
          vendorOption={vendorOptions[chosenVendorKey] ? vendorOptions[chosenVendorKey] : undefined}
          twoDayDeliverySw={vendorOptions[chosenVendorKey].twoDayDeliverySw}
          showSavings={format !== "reviewsPage"}
          hideEach={format === "reviewsPage"}
        />
      )}
      {availabilityState === "for-sale" && format !== "reviewsPage" && (
        <div
          id="bought-this-month-percent-recommended"
          className={pdpStyles.boughtThisMonthPercentRecommendedContainer}
        >
          <BoughtThisMonth productDetails={productDetails} />
        </div>
      )}
      <div id="vendor-picker-dropdown" className="pb-1">
        {availabilityState === "for-sale" && vendorOptions && vendorOptions.length > 0 && isReviewsFormat && (
          <VendorSelector
            vendorOptions={vendorOptions}
            chosenVendorKey={chosenVendorKey}
            setChosenVendorKey={changeVendorHandler}
            quantity={quantity}
          />
        )}
        {availabilityState === "for-sale" && vendorOptions && vendorOptions.length > 0 && !isReviewsFormat && (
          <VendorTiles
            vendorOptions={vendorOptions}
            chosenVendorKey={chosenVendorKey}
            setChosenVendorKey={changeVendorHandler}
            quantity={quantity}
            sponsoredVendorId={resolvedSponsoredVendorId}
            isDefaultVendor={
              numericSponsoredVendorId !== undefined && !isManufacturerSponsor(numericSponsoredVendorId.toString())
            }
            productBuyBoxPreference={productBuyBoxPreference}
            isCalculating={isCalculating}
            initialChosenVendorKey={initialChosenVendorKey}
            hasVidFromServer={hasVidFromServer}
            onSeeAllOptions={() => setVendorOffersExpanded(true)}
          />
        )}
      </div>
      {availabilityState === "for-sale" && vendorOptions && format !== "reviewsPage" && (
        <DeliveryToAndLocation
          id="delivery-to-and-location"
          variant="productDetail"
          deliveryToUser={deliveryData}
          updateLocationDetails={refreshDeliveryToUser}
        />
      )}
      {(availabilityState === "for-sale" ||
        availabilityState === "not-for-sale" ||
        availabilityState === "out-of-stock") &&
        variantGroupDetails != null && (
          <ProductVariantPicker
            variantGroupDetails={variantGroupDetails}
            onVariantNavigationStart={onVariantNavigationStart}
            variantNavigationLocked={isVariantNavigationPending}
          />
        )}
      <React.Fragment>
        <div
          id="calculating-message-cta"
          className={`calculating-message invisible ${
            isCalculating === true || isVariantNavigationPending ? "is-calculating" : ""
          }${isReviewsFormat ? ` ${styles.calculatingMessage}` : ""}${
            isReviewsFormat && (isCalculating === true || isVariantNavigationPending)
              ? ` ${styles.calculatingMessageActive}`
              : ""
          }`}
        >
          <Preloader textUnderAnimation="Calculating..." className="calculating-preloader" />
        </div>
        {!isReviewsFormat && (
          <div
            id="calculating-message-vendor-rows"
            className={`calculating-message invisible ${isCalculating === true ? "is-calculating" : ""}`}
          >
            <Preloader textUnderAnimation="Calculating..." className="calculating-preloader" />
          </div>
        )}
      </React.Fragment>
      <div id="shadow-cta" ref={callToActionRef} />
      {availabilityState === "for-sale" && vendorOptions && (
        <CallToAction
          quantity={quantity}
          maxInventoryAvailable={maxInventoryAvailable}
          setQuantity={updateQuantity}
          isAutoOrderEnabled={productDetails.autoOrderEnabled && format !== "reviewsPage"}
          vendorOptions={vendorOptions}
          subscriptionFutureDates={productSubscriptionFutureDates}
          addToCartCallback={addToCart}
          addToAutoOrderCallback={addToAutoOrder}
          justAddedToAutoOrder={justAddedToAutoOrder}
          productBeingAddedToCart={isAddingToCart}
          vendorProductBeingAddedToCart={vendorProductBeingAddedToCart}
          chosenVendorKey={chosenVendorKey}
          chosenAutoOrderLevel={chosenAutoOrderLevel}
          setChosenAutoOrderLevel={setChosenAutoOrderLevel}
          showExternalAddToCartError={showAddToCartErrorOnCalltoAction}
          disableExternalAddToCartErrorCallBack={setShowAddToCartErrorOnCallToAction}
          format={format}
        />
      )}
      {availabilityState === "for-sale" && vendorOptions && vendorOptions.length > 0 && format !== "reviewsPage" && (
        <VendorOptions
          totalVendorOptions={vendorOptions.length}
          expanded={vendorOffersExpanded}
          onExpandedChange={setVendorOffersExpanded}
        >
          {numericSponsoredVendorId !== undefined &&
            moveSponsorVendorOptions().map((vendorOption, index) => {
              return (
                <VendorOption
                  key={vendorOption.vendorProductId}
                  vendorOption={vendorOption}
                  index={index}
                  addToCartCallback={addToCart}
                  manufacturerOrCategoryLicenseIsRequired={
                    productDetails.mpLicenseExist || productDetails.catLicenseExist
                  }
                  productBeingAddedToCart={isAddingToCart}
                  vendorProductBeingAddedToCart={vendorProductBeingAddedToCart}
                  quantity={quantity}
                />
              );
            })}
          {numericSponsoredVendorId === undefined &&
            vendorOptions.map((vendorOption, index) => {
              return (
                <VendorOption
                  key={vendorOption.vendorProductId}
                  vendorOption={vendorOption}
                  index={index}
                  addToCartCallback={addToCart}
                  manufacturerOrCategoryLicenseIsRequired={
                    productDetails.mpLicenseExist || productDetails.catLicenseExist
                  }
                  productBeingAddedToCart={isAddingToCart}
                  vendorProductBeingAddedToCart={vendorProductBeingAddedToCart}
                  quantity={quantity}
                />
              );
            })}
        </VendorOptions>
      )}
      {availabilityState === "for-sale" && vendorOptions && vendorOptions.length > 0 && format !== "reviewsPage" && (
        <AddToShoppingList
          user={user}
          isInShoppingList={inShoppingList}
          mpId={mpId}
          onSubmitToShoppingList={onSubmitToShoppingListBackEnd}
        />
      )}
      {format !== "reviewsPage" && (
        <QuestionsAndAnswersSection
          mpId={mpId}
          productDetails={productDetails}
          bestVendorOption={vendorOptions[chosenVendorKey]}
        />
      )}
    </>
  );
};

const AB_TASTY_ADD_TO_CART_TRACKER = "PDP Add to Cart";

type VendorPickerProps = {
  availabilityState: string;
  enableBestDealBadge?: boolean;
  productDetails: ProductDetails;
  vendorOptionsFromServer: VendorOptionState[];
  initialChosenVendorKey?: number;
  hasVidFromServer?: boolean;
  qty: number;
  isOfferingsVisible?: boolean;
  categoriesTree: CategoryTree[];
  mpId: number;
  retailPrice: number;
  maxInventoryAvailable: number;
  productSubscriptionFutureDates: SubscriptionFutureDatesForProduct[];
  deliveryToUser?: DeliveryToUser;
  variantGroupDetails: ProductVariantResponse | null;
  manufacturer?: string;
  productBuyBoxPreference?: BuyBoxPreference[];
  sellProductEligibility?: SellProductEligibility | null;
  format?: ProductDisplayFormat;
};

export default ProductDetailClientPage;
